import { canCreateSpaces } from "@circle-react/helpers/spaceGroupHelpers";
import { spaceGroupPaths } from "@circle-react/helpers/urlHelpers";
import { useAnyHoverMediaQuery } from "@circle-react/hooks/useMediaQuery";
import type { SpaceGroup } from "@circle-react/types/SpaceGroup";
import classNames from "classnames";
import { forwardRef, useState } from "react";
import type { ReactNode } from "react";

import { AddOptions } from "./AddOptions";
import { SpaceGroupName } from "./SpaceGroupName";
import { SpaceGroupOptionsDropdown } from "./SpaceGroupOptionsDropdown";
import { SpaceGroupViewWrapper } from "./SpaceGroupViewWrapper";

export interface SpaceGroupViewProps {
  canManage?: boolean;
  children?: ReactNode;
  className?: string;
  id: string;
  isAdmin?: boolean;
  isCollapsed?: boolean;
  isDragging?: boolean;
  onAddMembersClick?: () => void;
  onCollapseToggle?: () => void;
  onCreateSpaceClick?: () => void;
  onCreateSpaceGroupClick?: () => void;
  onEditSpaceGroupClick?: () => void;
  position?: number;
  shouldDisplayOptions?: boolean;
  spaceGroup: SpaceGroup;
  style?: React.CSSProperties;
  viewWrapperRef?: (element: HTMLElement | null) => void;
}

export const SpaceGroupView = forwardRef<HTMLDivElement, SpaceGroupViewProps>(
  (
    {
      canManage = false,
      children = null,
      isAdmin = false,
      isCollapsed = false,
      isDragging = false,
      onAddMembersClick = () => {},
      onCollapseToggle = () => {},
      onCreateSpaceClick = () => {},
      onCreateSpaceGroupClick = () => {},
      onEditSpaceGroupClick = () => {},
      position,
      shouldDisplayOptions = true,
      spaceGroup,
      viewWrapperRef,
      className,
      ...rest
    },
    ref,
  ) => {
    const canHover = useAnyHoverMediaQuery();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    return (
      <div
        ref={ref}
        className={classNames("group relative", className)}
        {...rest}
        role={undefined}
        tabIndex={-1}
      >
        <SpaceGroupViewWrapper
          onCollapseToggle={onCollapseToggle}
          isDragging={isDragging}
          isCollapsed={isCollapsed}
          ref={viewWrapperRef}
        >
          <SpaceGroupName
            isDropdownOpen={isDropdownOpen}
            canManage={canManage}
            spaceGroup={spaceGroup}
            isCollapsed={isCollapsed}
          />
        </SpaceGroupViewWrapper>
        {shouldDisplayOptions && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- onClick only used to prevent propagation
          <div
            className={classNames(
              "text-c-sidebar absolute right-[1rem] top-[0.375rem] ml-1 flex gap-1 transition-opacity duration-75",
              {
                "invisible group-focus-within:visible group-hover:visible":
                  canHover && !isDropdownOpen,
              },
            )}
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
            }}
            data-testid="space-group-actions"
          >
            <SpaceGroupOptionsDropdown
              onAddMembersClick={onAddMembersClick}
              onEditSpaceGroupClick={onEditSpaceGroupClick}
              seeMembersLink={spaceGroupPaths.members({
                spaceGroupSlug: spaceGroup.slug,
              })}
              seeSpacesLink={spaceGroupPaths.show({
                spaceGroupSlug: spaceGroup.slug,
              })}
              onToggle={setIsDropdownOpen}
              canManage={canManage}
            />
            {(canCreateSpaces({ spaceGroup }) || isAdmin) && (
              <AddOptions
                position={position}
                isAdmin={isAdmin}
                onCreateSpace={onCreateSpaceClick}
                onCreateSpaceGroup={onCreateSpaceGroupClick}
                canCreateSpaces={canCreateSpaces({ spaceGroup })}
                onToggle={setIsDropdownOpen}
              />
            )}
          </div>
        )}
        {!isCollapsed && children}
      </div>
    );
  },
);

SpaceGroupView.displayName = "SpaceGroupView";
